import {
    activateUser,
    createUser,
    destroyUser,
    getAdmins,
    getBoxData,
    getUser,
    getUsers,
    getUsersList,
    importUsers,
    searchUserByEmail,
    setUserAsAdmin,
    updateUser
} from '@/api/users';

export default {
    namespaced: true,
    state: {
        boxData: {},
        usersList: [],
        users: [],
        usersMeta: {total: 0},
        admins: [],
        adminsMeta: {total: 0},
        /* Les compteurs des onglets, séparés des `meta` des listes. Deux raisons :
         * — une liste ne se charge que quand SON onglet est ouvert, alors que les compteurs
         *   restent visibles depuis tous les onglets ;
         * — `null` veut dire « pas encore su », là où `{total: 0}` affirme « zéro ». Un
         *   administrateur qui lit « Admins (0) » en arrivant croit avoir perdu ses admins. */
        usersCount: null,
        adminsCount: null,
        user: null,
        searchedUser: null,
    },
    getters: {
        BOX_DATA(state) {
            return state.boxData;
        },
        USERS_LIST(state) {
            return state.usersList;
        },
        USERS(state) {
            return state.users;
        },
        USERS_META(state) {
            return state.usersMeta;
        },
        ADMINS(state) {
            return state.admins;
        },
        ADMINS_META(state) {
            return state.adminsMeta;
        },
        USERS_COUNT(state) {
            return state.usersCount;
        },
        ADMINS_COUNT(state) {
            return state.adminsCount;
        },
        USER(state) {
            return state.user;
        },
        SEARCHED_USER(state) {
            return state.searchedUser;
        }
    },
    mutations: {
        SET_BOX_DATA(state, data) {
            state.boxData = data;
        },
        SET_USERS_LIST(state, data) {
            state.usersList = data;
        },
        SET_USERS(state, data) {
            state.users = data;
        },
        SET_USERS_META(state, data) {
            state.usersMeta = data;
        },
        SET_ADMINS(state, data) {
            state.admins = data;
        },
        SET_ADMINS_META(state, data) {
            state.adminsMeta = data;
        },
        SET_USERS_COUNT(state, data) {
            state.usersCount = data;
        },
        SET_ADMINS_COUNT(state, data) {
            state.adminsCount = data;
        },
        SET_USER(state, data) {
            state.user = data;
        },
        SET_SEARCHED_USER(state, data) {
            state.searchedUser = data;
        },
    },
    actions: {
        GET_BOX_DATA(context, organizationId) {
            return new Promise((resolve, reject) => {
                getBoxData(organizationId).then(response => {
                    context.commit('SET_BOX_DATA', response.data)
                    resolve(response)
                }).catch(error => {
                    reject(error)
                })
            })
        },
        GET_USERS_LIST(context, filters) {
            return new Promise((resolve, reject) => {
                getUsersList(filters).then(response => {
                    context.commit('SET_USERS_LIST', response.data)
                    resolve(response)
                }).catch(error => {
                    reject(error)
                })
            })
        },
        /* Ne rapporte QUE le total (per_page=1) : le compteur d'un onglet ne doit jamais
         * dépendre de l'ouverture de cet onglet, ni ramener une page entière pour un nombre.
         * En cas d'échec on repasse à `null` — mieux vaut ne rien afficher qu'un faux zéro. */
        GET_USERS_COUNT(context, filters) {
            return getUsers({...filters, page: 1, per_page: 1})
                .then(response => {
                    const total = response?.meta?.total ?? null;
                    context.commit('SET_USERS_COUNT', total);
                    return total;
                })
                .catch(() => {
                    context.commit('SET_USERS_COUNT', null);
                    return null;
                });
        },
        GET_ADMINS_COUNT(context, filters) {
            return getAdmins({...filters, page: 1, per_page: 1})
                .then(response => {
                    const total = response?.meta?.total ?? null;
                    context.commit('SET_ADMINS_COUNT', total);
                    return total;
                })
                .catch(() => {
                    context.commit('SET_ADMINS_COUNT', null);
                    return null;
                });
        },
        GET_USERS(context, data) {
            return new Promise((resolve, reject) => {
                getUsers(data).then(response => {
                    context.commit('SET_USERS', response.data);
                    context.commit('SET_USERS_META', response.meta);
                    context.commit('SET_USERS_COUNT', response.meta?.total ?? null);
                    resolve(response);
                }).catch(error => {
                    reject(error)
                })
            })
        },
        GET_ADMINS(context, filters) {
            return new Promise((resolve, reject) => {
                getAdmins(filters).then(response => {
                    context.commit('SET_ADMINS', response.data);
                    context.commit('SET_ADMINS_META', response.meta);
                    context.commit('SET_ADMINS_COUNT', response.meta?.total ?? null);
                    resolve(response);
                }).catch(error => {
                    reject(error)
                })
            })
        },
        GET_USER(context, slug) {
            return new Promise((resolve, reject) => {
                getUser(slug).then(response => {
                    context.commit('SET_USER', response.data);
                    resolve(response);
                }).catch(error => {
                    reject(error)
                })
            })
        },
        CREATE_USER(context, data) {
            return new Promise((resolve, reject) => {
                createUser(data).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error)
                })
            })
        },
        UPDATE_USER(context, {slug, data}) {
            return new Promise((resolve, reject) => {
                updateUser(slug, data).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error)
                })
            })
        },
        ACTIVATE_USER(context, slug) {
            return new Promise((resolve, reject) => {
                activateUser(slug).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error)
                })
            })
        },
        DESTROY_USER(context, slug) {
            return new Promise((resolve, reject) => {
                destroyUser(slug).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error)
                })
            })
        },
        SEARCH_USER_BY_EMAIL(context, email) {
            return new Promise((resolve, reject) => {
                searchUserByEmail(email).then(response => {
                    context.commit('SET_SEARCHED_USER', response.data);
                    resolve(response);
                }).catch(error => {
                    context.commit('SET_SEARCHED_USER', null);
                    reject(error);
                })
            })
        },
        SET_USER_AS_ADMIN(context, data) {
            return new Promise((resolve, reject) => {
                setUserAsAdmin(data).then(response => {
                    resolve(response);
                }).catch(error => {
                    reject(error);
                })
            })
        },
        IMPORT(context, formData) {
            return new Promise((resolve, reject) => {
                importUsers(formData).then(response => {
                    resolve(response)
                }).catch(error => {
                    reject(error)
                })
            })
        }
    },
}